import { useTextDirection } from '@vueuse/core'
import { ref, computed } from 'vue'

import helpers from '@/logic/helpers'

// Swipe configuration constants
const SWIPE_THRESHOLD = 80 // px to activate reply
const MAX_DISPLACEMENT = 100 // max visual displacement in px

/**
 * Trigger device haptic feedback if available
 */
function triggerHapticFeedback() {
  if (navigator.vibrate) {
    navigator.vibrate(50)
  }
}

/**
 * Composable for handling swipe-to-reply gesture on touch devices
 * @param {Function} onSwipeComplete - Callback fired when swipe threshold is reached
 * @param {Function} canSwipe - Function that returns whether swiping is allowed
 * @param {Function} isOwnMessage - Function that returns whether this is user's own message
 * @returns {Object} Touch handlers and reactive state
 */
export function useSwipeToReply(onSwipeComplete, canSwipe = () => true, isOwnMessage = () => false) {
  const textDirection = useTextDirection()
  const startX = ref(0)
  const startY = ref(0)
  const currentX = ref(0)
  const isSwiping = ref(false)
  const isHorizontalSwipe = ref(false)
  const hasTriggeredHaptic = ref(false)

  /**
     * Get swipe direction multiplier based on message ownership and text direction
     * Own messages: swipe toward center (left in LTR, right in RTL)
     * Other's messages: swipe toward center (right in LTR, left in RTL)
     */
  const getSwipeDirection = () => {
    const isRtl = textDirection.value === 'rtl'
    const isOwn = isOwnMessage()
    // Own message in LTR: swipe left (-1), Other in LTR: swipe right (1)
    // Own message in RTL: swipe right (1), Other in RTL: swipe left (-1)
    if (isOwn) {
      return isRtl ? 1 : -1
    }
    return isRtl ? -1 : 1
  }

  /**
     * Calculate the swipe distance, clamped between 0 and MAX_DISPLACEMENT
     * Direction depends on message ownership and RTL setting
     */
  const swipeDistance = computed(() => {
    if (!isSwiping.value) {
      return 0
    }
    const rawDelta = currentX.value - startX.value
    const direction = getSwipeDirection()
    const adjustedDelta = rawDelta * direction
    return Math.max(0, Math.min(adjustedDelta, MAX_DISPLACEMENT))
  })

  /**
         * Progress from 0 to 1+ indicating how close to threshold
         */
  const swipeProgress = computed(() => {
    return Math.min(swipeDistance.value / SWIPE_THRESHOLD, 1.5)
  })

  /**
         * Whether the swipe has passed the activation threshold
         */
  const isPastThreshold = computed(() => {
    return swipeDistance.value >= SWIPE_THRESHOLD
  })

  /**
         * Handle touch start - initialize swipe tracking
         */
  const handleTouchStart = (event) => {
    if (!helpers.isTouchDevice()) {
      return
    }
    if (!canSwipe()) {
      return
    }
    startX.value = event.touches[0].clientX
    startY.value = event.touches[0].clientY
    currentX.value = startX.value
    isSwiping.value = true
    isHorizontalSwipe.value = false
    hasTriggeredHaptic.value = false
  }

  /**
         * Handle touch move - update position and trigger haptic at threshold
         */
  const handleTouchMove = (event) => {
    if (!isSwiping.value) {
      return
    }

    const touchX = event.touches[0].clientX
    const touchY = event.touches[0].clientY
    const deltaX = Math.abs(touchX - startX.value)
    const deltaY = Math.abs(touchY - startY.value)

    // Determine swipe direction on first significant movement
    if (!isHorizontalSwipe.value && (deltaX > 10 || deltaY > 10)) {
      isHorizontalSwipe.value = deltaX > deltaY
    }

    // If vertical swipe, stop tracking and allow scroll
    if (!isHorizontalSwipe.value && deltaY > 10) {
      isSwiping.value = false
      return
    }

    // Prevent scroll when swiping horizontally
    if (isHorizontalSwipe.value) {
      event.preventDefault()
    }

    currentX.value = touchX

    // Trigger haptic feedback when crossing threshold (once per swipe)
    if (isPastThreshold.value && !hasTriggeredHaptic.value) {
      triggerHapticFeedback()
      hasTriggeredHaptic.value = true
    }
  }

  /**
         * Handle touch end - complete swipe or spring back
         */
  const handleTouchEnd = () => {
    if (!isSwiping.value) {
      return
    }

    if (isPastThreshold.value) {
      onSwipeComplete()
    }

    // Reset state (animation handled by CSS transition)
    startX.value = 0
    startY.value = 0
    currentX.value = 0
    isSwiping.value = false
    isHorizontalSwipe.value = false
    hasTriggeredHaptic.value = false
  }

  return {
    // Event handlers
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    // Reactive state
    swipeDistance,
    swipeProgress,
    isSwiping,
    isPastThreshold,
    // Constants (for external use if needed)
    SWIPE_THRESHOLD,
    MAX_DISPLACEMENT,
  }
}
